// Bin Transfer form — move qty from one bin/godown to another. Uses
// /api/stock/on-hand to pick a source lot, then posts to /api/stock/bin-transfer.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import KindBadge from "@/components/KindBadge";

export default function BinTransfer() {
  const [rms, setRms] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [bins, setBins] = useState([]);
  const [lots, setLots] = useState([]);
  const [rmId, setRmId] = useState("");
  const [srcLotId, setSrcLotId] = useState("");
  const [dstGodownId, setDstGodownId] = useState("");
  const [dstBinId, setDstBinId] = useState("");
  const [qty, setQty] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: r }, { data: g }, { data: b }] = await Promise.all([
        api.get("/raw-materials/"), api.get("/godowns/"), api.get("/bin-locations/").catch(() => ({ data: [] })),
      ]);
      // Mar 2026 · Universal Deactivation Filter — hide inactive RMs
      // from the picker (historical stock still visible in Kardex).
      setRms((r || []).filter(x => x.is_active !== false)); setGodowns(g); setBins(b || []);
    })();
  }, []);
  useEffect(() => {
    if (!rmId) { setLots([]); return; }
    (async () => {
      const { data } = await api.get(`/stock/on-hand?rm_id=${rmId}&quality_status=Passed`);
      setLots(data?.rows || []);
    })();
  }, [rmId]);

  // Feb 2026 · O(1) godown lookup — used to enrich the source-lot label
  // (kind badge + godown name) and the destination picker.
  const godownById = useMemo(() => {
    const m = {};
    for (const g of godowns) m[g.id] = g;
    return m;
  }, [godowns]);

  const submit = async () => {
    if (!srcLotId || !dstGodownId || qty <= 0) { toast.error("Pick a source lot, destination godown, and qty > 0"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/stock/bin-transfer", {
        src_lot_id: srcLotId, dst_godown_id: dstGodownId, dst_bin_id: dstBinId, quantity: qty, remarks,
      });
      toast.success(`Bin transfer posted · dst lot ${data.dst_lot_id?.slice(0, 8)}…`);
      setSrcLotId(""); setDstGodownId(""); setDstBinId(""); setQty(0); setRemarks("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed"); }
    setBusy(false);
  };

  const inp = "w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
  return (
    <div className="space-y-4" data-testid="bin-transfer-page">
      <div className="overline text-[10px] text-[var(--accent)]">INVENTORY</div>
      <h1 className="text-2xl font-semibold">Bin Transfer</h1>
      <p className="text-sm text-zinc-400">Move stock from one bin/godown to another. Creates paired Bin-Transfer Out + In txns.</p>
      <div className="tactical-card p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Raw Material *</div>
            <select data-testid="bt-rm" value={rmId} onChange={e => setRmId(e.target.value)} className={inp}>
              <option value="">— Select —</option>
              {rms.map(r => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}
            </select>
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Source Lot * ({lots.length} available)</div>
            <select data-testid="bt-src-lot" value={srcLotId} onChange={e => setSrcLotId(e.target.value)} className={inp} disabled={!rmId}>
              <option value="">— Select —</option>
              {lots.map(l => {
                const g = godownById[l.godown_id] || {};
                return <option key={l.lot_id} value={l.lot_id}>{l.lot_no} · qty {l.available_qty} · [{(g.kind || "?").toUpperCase()}] {g.name || "no godown"}</option>;
              })}
            </select>
            {srcLotId && (() => {
              const l = lots.find(x => x.lot_id === srcLotId);
              const g = l && godownById[l.godown_id];
              return g ? <div className="mt-1 flex items-center gap-1.5" data-testid="bt-src-kind"><KindBadge kind={g.kind} /><span className="text-[10px] text-zinc-500 normal-case tracking-normal">{g.name}</span></div> : null;
            })()}
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Destination Godown *</div>
            <select data-testid="bt-dst-godown" value={dstGodownId} onChange={e => setDstGodownId(e.target.value)} className={inp}>
              <option value="">— Select —</option>
              {godowns.map(g => <option key={g.id} value={g.id}>[{(g.kind || "?").toUpperCase()}] {g.name}</option>)}
            </select>
            {dstGodownId && godownById[dstGodownId] && (
              <div className="mt-1 flex items-center gap-1.5" data-testid="bt-dst-kind"><KindBadge kind={godownById[dstGodownId].kind} /><span className="text-[10px] text-zinc-500 normal-case tracking-normal">{godownById[dstGodownId].name}</span></div>
            )}
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Destination Bin</div>
            <select data-testid="bt-dst-bin" value={dstBinId} onChange={e => setDstBinId(e.target.value)} className={inp}>
              <option value="">— None —</option>
              {bins.filter(b => !dstGodownId || b.godown_id === dstGodownId).map(b => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
            </select>
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Quantity *</div>
            <input data-testid="bt-qty" type="number" step="0.001" min="0" value={qty} onChange={e => setQty(parseFloat(e.target.value)||0)} className={inp} />
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Remarks</div>
            <input data-testid="bt-remarks" value={remarks} onChange={e => setRemarks(e.target.value)} className={inp} />
          </label>
        </div>
        <div className="flex flex-col items-end gap-1 pt-2">
          {(() => {
            // Feb 2026 · Negative-Stock Prevention · lot-based bin transfer.
            // Compare qty against the source lot's available_qty; if short,
            // disable Post and surface a matching error message.
            const srcLot = lots.find(l => l.lot_id === srcLotId);
            const srcGod = godownById[srcLot?.godown_id] || {};
            const available = Number(srcLot?.available_qty || 0);
            const short = srcLot && qty > available + 1e-4;
            const noPick = !srcLotId || !dstGodownId || qty <= 0;
            const disabled = busy || noPick || short;
            return (
              <>
                <button
                  data-testid="bt-submit"
                  onClick={submit}
                  disabled={disabled}
                  title={short ? `Insufficient stock in ${srcGod.name || 'source godown'}` : ""}
                  className={`h-10 px-6 text-white text-sm font-semibold rounded-sm ${disabled ? "bg-zinc-700 cursor-not-allowed" : "bg-[var(--accent)] hover:bg-blue-600"}`}
                >
                  {busy ? "Posting…" : "Post Transfer"}
                </button>
                {short && (
                  <div data-testid="bt-stock-error" className="text-[10px] text-red-300 font-mono text-right max-w-xs">
                    Insufficient stock in {srcGod.name || "source godown"}. Current balance is {available}. Cannot proceed with issue.
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
